import { sql } from "drizzle-orm";
import type { WorkflowV2DbLike, WorkflowV2RunRow } from "./workflow-kernel-v2-store.js";
import { executeWorkflowV2Query } from "./workflow-kernel-v2-db.js";

export type WorkflowV2DeadlineResult = {
  requeued: WorkflowV2RunRow[];
  failed: WorkflowV2RunRow[];
};

function rowsFromResult<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  if (result && typeof result === "object" && Array.isArray((result as { rows?: unknown[] }).rows)) {
    return (result as { rows: T[] }).rows;
  }
  return [];
}

export async function terminalizeRequestedWorkflowV2Cancellations(
  db: WorkflowV2DbLike,
  limit = 100,
): Promise<WorkflowV2RunRow[]> {
  const result = await executeWorkflowV2Query(
    db,
    sql`
        WITH candidate AS (
            SELECT id
            FROM _gencow_workflow_runs_v2
            WHERE cancel_requested_at IS NOT NULL
              AND completed_at IS NULL
              AND (
                  status IN ('queued', 'sleeping', 'waiting')
                  OR (
                      status = 'running'
                      AND (lease_expires_at IS NULL OR lease_expires_at < NOW())
                  )
              )
            ORDER BY cancel_requested_at ASC, created_at ASC
            FOR UPDATE SKIP LOCKED
            LIMIT ${limit}
        )
        UPDATE _gencow_workflow_runs_v2 run
        SET
            status = 'cancelled',
            error_code = 'cancelled',
            error_json = jsonb_build_object('message', 'Cancelled by user'),
            lease_owner = NULL,
            lease_expires_at = NULL,
            heartbeat_at = NULL,
            active_started_at = NULL,
            active_deadline_at = NULL,
            completed_at = NOW(),
            updated_at = NOW()
        WHERE run.id IN (SELECT id FROM candidate)
        RETURNING run.*
    `,
  );
  return rowsFromResult<WorkflowV2RunRow>(result);
}

export async function processWorkflowV2ExpiredDeadlines(
  db: WorkflowV2DbLike,
  limit = 100,
): Promise<WorkflowV2DeadlineResult> {
  const activeRetryResult = await executeWorkflowV2Query(
    db,
    sql`
        WITH candidate AS (
            SELECT id, retry_count
            FROM _gencow_workflow_runs_v2
            WHERE status = 'running'
              AND active_deadline_at IS NOT NULL
              AND active_deadline_at <= NOW()
              AND retry_count < max_retries
              AND completed_at IS NULL
            ORDER BY active_deadline_at ASC, created_at ASC
            FOR UPDATE SKIP LOCKED
            LIMIT ${limit}
        ),
        requeued AS (
            UPDATE _gencow_workflow_runs_v2 run
            SET
                status = 'queued',
                retry_count = run.retry_count + 1,
                error_code = 'active_timeout',
                error_json = jsonb_build_object(
                    'message',
                    'Workflow active execution deadline exceeded; retry scheduled',
                    'activeDeadlineAt',
                    run.active_deadline_at,
                    'retryable',
                    true
                ),
                runnable_at = NOW() + (
                    (30000 * POWER(3, GREATEST(0, candidate.retry_count))) * INTERVAL '1 millisecond'
                ),
                lease_owner = NULL,
                lease_expires_at = NULL,
                heartbeat_at = NULL,
                active_started_at = NULL,
                active_deadline_at = NULL,
                completed_at = NULL,
                updated_at = NOW()
            FROM candidate
            WHERE run.id = candidate.id
            RETURNING run.*
        ),
        wake_outbox AS (
            INSERT INTO _gencow_workflow_outbox_v2 (
                id,
                run_id,
                kind,
                available_at,
                status
            )
            SELECT
                'deadline-retry:' || id || ':' || retry_count,
                id,
                'wake_retry',
                runnable_at,
                'pending'
            FROM requeued
            ON CONFLICT (id) DO NOTHING
            RETURNING id
        )
        SELECT *
        FROM requeued
    `,
  );
  const activeFailureResult = await executeWorkflowV2Query(
    db,
    sql`
        WITH candidate AS (
            SELECT id
            FROM _gencow_workflow_runs_v2
            WHERE status = 'running'
              AND active_deadline_at IS NOT NULL
              AND active_deadline_at <= NOW()
              AND retry_count >= max_retries
              AND completed_at IS NULL
            ORDER BY active_deadline_at ASC, created_at ASC
            FOR UPDATE SKIP LOCKED
            LIMIT ${limit}
        )
        UPDATE _gencow_workflow_runs_v2 run
        SET
            status = 'failed',
            error_code = 'active_timeout',
            error_json = jsonb_build_object(
                'message',
                'Workflow active execution deadline exceeded',
                'activeDeadlineAt',
                active_deadline_at,
                'retryable',
                false
            ),
            lease_owner = NULL,
            lease_expires_at = NULL,
            heartbeat_at = NULL,
            completed_at = NOW(),
            updated_at = NOW()
        WHERE run.id IN (SELECT id FROM candidate)
        RETURNING run.*
    `,
  );
  const lifecycleResult = await executeWorkflowV2Query(
    db,
    sql`
        WITH candidate AS (
            SELECT id
            FROM _gencow_workflow_runs_v2
            WHERE status IN ('queued', 'running', 'sleeping', 'waiting')
              AND lifecycle_deadline_at IS NOT NULL
              AND lifecycle_deadline_at <= NOW()
              AND completed_at IS NULL
              AND NOT (
                status IN ('queued', 'running')
                AND current_step_key IS NULL
                AND active_started_at IS NULL
              )
            ORDER BY lifecycle_deadline_at ASC, created_at ASC
            FOR UPDATE SKIP LOCKED
            LIMIT ${limit}
        )
        UPDATE _gencow_workflow_runs_v2 run
        SET
            status = 'failed',
            error_code = 'lifecycle_timeout',
            error_json = jsonb_build_object(
                'message',
                'Workflow lifecycle deadline exceeded',
                'lifecycleDeadlineAt',
                lifecycle_deadline_at
            ),
            lease_owner = NULL,
            lease_expires_at = NULL,
            heartbeat_at = NULL,
            active_started_at = NULL,
            active_deadline_at = NULL,
            completed_at = NOW(),
            updated_at = NOW()
        WHERE run.id IN (SELECT id FROM candidate)
        RETURNING run.*
    `,
  );
  return {
    requeued: rowsFromResult<WorkflowV2RunRow>(activeRetryResult),
    failed: [
      ...rowsFromResult<WorkflowV2RunRow>(activeFailureResult),
      ...rowsFromResult<WorkflowV2RunRow>(lifecycleResult),
    ],
  };
}

export async function failWorkflowV2ExpiredDeadlines(
  db: WorkflowV2DbLike,
  limit = 100,
): Promise<WorkflowV2RunRow[]> {
  const result = await processWorkflowV2ExpiredDeadlines(db, limit);
  return result.failed;
}
